/**
 * Evidence freshness-probe pill helper (P21; lifted to shared/ in P75).
 *
 * `formatProbePill` maps the P21 freshness-probe columns
 * (last_probe_status / last_probed_at / last_probe_error) onto a
 * `{ tone, label, tooltip }` triple, or null when there is nothing to
 * show. Single source of truth for the freshness UI, consumed by both
 * the framework CycleAssessmentGridView evidence rows and the twin
 * EvidenceArtefactDetail page -- which is why it lives in shared/
 * (twin/ must not import from framework/).
 */

export function formatProbePill(
  status: "fresh" | "failed" | "pending" | null,
  lastProbedAt: string | null,
  lastProbeError: string | null,
): { tone: string; label: string; tooltip: string } | null {
  if (status === null && lastProbedAt === null) {
    return {
      tone: "bg-slate-100 text-slate-700 ring-1 ring-slate-300",
      label: "not verified",
      tooltip: "No freshness probe has run against this artefact yet.",
    };
  }
  if (status === "fresh") {
    return {
      tone: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300",
      label: `verified ${formatRelativeTime(lastProbedAt)}`,
      tooltip: `Last verified at ${lastProbedAt ?? "(unknown)"}.`,
    };
  }
  if (status === "failed") {
    // E6 -- "broken" is a claim about the BANK'S evidence, and a probe
    // that could not leave our own network has not established
    // anything about it. The walkthrough read
    // "broken: network: ProxyError" against a perfectly good artefact
    // behind a sandbox proxy, which is us reporting our own plumbing
    // as the bank's failure.
    const reason = lastProbeError ?? "unknown error";
    if (unreachable(reason)) {
      return {
        tone: "bg-amber-50 text-amber-800 ring-1 ring-amber-300",
        label: "could not be checked",
        tooltip:
          `The probe at ${lastProbedAt ?? "(unknown)"} could not reach the ` +
          `network: ${reason}. This says nothing about the artefact.`,
      };
    }
    return {
      tone: "bg-rose-50 text-rose-800 ring-1 ring-rose-300",
      label: `broken: ${reason}`,
      tooltip: `Last probe at ${lastProbedAt ?? "(unknown)"} failed: ${reason}.`,
    };
  }
  // status === "pending" or null with lastProbedAt set.
  return {
    tone: "bg-amber-50 text-amber-800 ring-1 ring-amber-300",
    label: "probe pending",
    tooltip: "A probe attempt is queued or in flight.",
  };
}

/**
 * Did the probe fail to REACH anything, rather than find something
 * wrong at the far end?
 *
 * Deliberately a narrow list of transport failures. Anything not
 * recognised stays "broken", because under-reporting a genuinely dead
 * evidence link is the more dangerous mistake of the two.
 */
export function unreachable(error: string): boolean {
  return /proxy|dns|name resolution|connection refused|timed? ?out|unreachable|ssl|certificate/i.test(
    error,
  );
}

export function formatRelativeTime(iso: string | null): string {
  if (iso === null) return "just now";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
